import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { formatYAxisValue } from '../../Components/DrillDown/formatters';
import { HorizontalBarChartSkeleton } from '../../Components/Skeletons/ChartSkeletons';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme
} from '@mui/material';

type PerdasHistoricoData = {
  periodo: string;
  total_perda: number;
};

type Props = {
  data: PerdasHistoricoData[];
  loading?: boolean;
  title?: string;
};

const PerdasPorMesChart: React.FC<Props> = ({
  data,
  loading = false,
  title = 'Histórico de Perdas por Mês (Últimos 12 meses)'
}) => {
  const theme = useTheme();

  // Validar e filtrar dados válidos
  const validData = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.filter(item => 
      item && 
      typeof item.periodo === 'string' && 
      item.periodo.trim() !== '' &&
      typeof item.total_perda === 'number' && 
      !isNaN(item.total_perda)
    );
  }, [data]);

  // Log para debug
  React.useEffect(() => {

  }, [data, validData]);

  return (
    <Card sx={{ borderRadius: 3, p: 2, overflow: 'visible' }}>
      <CardContent sx={{ p: 0, overflow: 'visible' }}>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ color: theme.palette.text.secondary, mb: 2 }}
        >
          {title}
        </Typography>

        {loading ? (
          <HorizontalBarChartSkeleton height={400} showTitle={false} />
        ) : !validData || validData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
            sx={{ color: theme.palette.text.secondary }}
          >
            <Typography variant="h6" gutterBottom>
              📊 Nenhum dado encontrado
            </Typography>
            <Typography variant="body2" align="center">
              Não há dados de perdas disponíveis para o período selecionado.
              <br />
              Verifique se o SPED fiscal possui registros de perdas.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 400, overflow: 'visible', position: 'relative', zIndex: 10 }}>
            <ResponsiveBar
              data={validData}
              keys={['total_perda']}
              indexBy="periodo"
              layout="vertical"
              valueFormat={(value) =>
                new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(value as number)
              }
              margin={{ top: 20, right: 20, bottom: 70, left: 70 }}
              padding={0.35}
              innerPadding={6}
              colors={theme.palette.error.main}
              borderColor={{ from: 'color', modifiers: [['darker', 1.4]] }}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -40
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                legend: 'Valor (R$)',
                legendPosition: 'middle',
                legendOffset: -60,
                format: formatYAxisValue
              }}
              labelSkipHeight={14}
              labelTextColor="#ffffff"
              labelFormat={(value) =>
                `R$ ${Number(value).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`
              }
              tooltip={({ value, id }) => (
                <Box
                  sx={{
                    background: theme.palette.background.paper,
                    borderRadius: 1,
                    boxShadow: 2,
                    p: 1
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(value as number)}
                  </Typography>
                </Box>
              )}
              theme={{
                labels: {
                  text: {
                    fontSize: 13,
                    fontWeight: 600
                  }
                }
              }}
              animate
              motionConfig="gentle"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PerdasPorMesChart;
